import MarketGroupTypes from './types';
import { isEmpty, uniqBy } from 'mojito/utils';

const { AGGREGATED_MARKET_TYPE, GROUP_TYPES } = MarketGroupTypes;

/**
 * Market groups data model utilities.
 *
 * @class MarketGroupsUtils
 * @name utils
 * @memberof Mojito.Services.SportsContent.MarketGroups
 */
export default class MarketGroupsUtils {
    /**
     * Creates aggregated market info object.
     *
     * @param {Map<string, Mojito.Services.SportsContent.Events.types.MarketLine>} marketLines - The map of market lines. Key is line type and value is market line that contains actual market id.
     * @param {Mojito.Services.EventGroups.types.MarketOption} marketOption - Market option. Will be associated with event.marketLines object to identify marketIds and selections mask for generated market info object.
     * @param {number} displayOrder - Display order.
     *
     * @returns {Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo|undefined} Generic aggregated market info object. Or undefined if no marketOption provided.
     * @function Mojito.Services.SportsContent.MarketGroups.utils.createMarketInfo
     */
    static createMarketInfo(marketLines, marketOption, displayOrder) {
        if (!marketOption) {
            return;
        }
        const marketLine = marketLines?.[marketOption.marketLineType] || {};
        return {
            id: `AGGREGATED_MARKET_TYPE.GENERIC-${displayOrder}`,
            name: 'Generic market',
            type: AGGREGATED_MARKET_TYPE.GENERIC,
            mask: marketOption.headers,
            markets: [{ id: marketLine.id }],
            fallbackMarketIds: marketLine.fallback ? [marketLine.id] : [],
            matchAcca: false,
            displayOrder,
        };
    }

    /**
     * Creates price boost aggregated market info object.
     *
     * @param {Array<string>} marketIds - List of market line ids.
     * @returns {Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo|undefined} Price boost aggregated market info object. Or undefined if marketIds is empty.
     * @function Mojito.Services.SportsContent.MarketGroups.utils.createPriceBoostedMarketInfo
     */
    static createPriceBoostedMarketInfo(marketIds) {
        if (isEmpty(marketIds)) {
            return;
        }

        return {
            id: AGGREGATED_MARKET_TYPE.PRICE_BOOST,
            name: 'Price boost',
            type: AGGREGATED_MARKET_TYPE.PRICE_BOOST,
            markets: marketIds.map(id => ({ id })),
            matchAcca: false,
        };
    }

    /**
     * Creates game line aggregated market info object.
     *
     * @param {Map<string, Mojito.Services.SportsContent.Events.types.MarketLine>} marketLines - The map of market lines. Key is line type and value is market line that contains actual market id.
     * @param {Mojito.Services.EventGroups.types.MarketOption} gameLine - Game line markets option. Will be associated with event.marketLines object to identify marketIds and selections mask for generated game line object.
     *
     * @returns {Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo|undefined} Game line aggregated market info object. Or undefined if gameLineOption is not provided.
     * @function Mojito.Services.SportsContent.MarketGroups.utils.createGameLineMarketInfo
     */
    static createGameLineMarketInfo(marketLines, gameLine) {
        if (!gameLine) {
            return;
        }
        const lines = marketLines || {};
        // marketOptions contains info about markets which are part of the game line a.k. MONEY, TOTAL, SPREAD.
        const marketOptions = gameLine.options || [];
        const markets = MarketGroupsUtils.getMarketIdsByMarketOption(lines, marketOptions).map(
            id => ({ id })
        );
        const matchAcca = marketOptions.some(option => lines[option.marketLineType]?.matchAcca);
        const mask = marketOptions.map(option => option.label);
        const allowCashout = marketOptions.some(
            option => lines[option.marketLineType]?.allowCashout
        );

        return {
            id: AGGREGATED_MARKET_TYPE.GAME_LINE,
            name: 'Game lines',
            type: AGGREGATED_MARKET_TYPE.GAME_LINE,
            mask,
            markets,
            matchAcca,
            allowCashout,
        };
    }

    /**
     * Resolves market ids from market lines.
     * Will go through each market option in provided list and resolve corresponding market id from market lines using marketOption.marketLineType property.
     *
     * @param {Map<string, Mojito.Services.SportsContent.Events.types.MarketLine>} marketLines - The map of market lines. Key is line type and value is market line that contains actual market id.
     * @param {Array<Mojito.Services.EventGroups.types.MarketOption>} [marketOptions = []] - Market option. Will be associated with event.marketLines object to identify marketIds and selections mask for generated game line object.
     *
     * @returns {Array<string>} List of market ids.
     * @function Mojito.Services.SportsContent.MarketGroups.utils.getMarketIdsByMarketOption
     */
    static getMarketIdsByMarketOption(marketLines, marketOptions = []) {
        return marketOptions.map(option => marketLines[option.marketLineType]?.id);
    }

    /**
     * Finds race market group id.
     *
     * @param {Array} marketGroups - Market groups.
     *
     * @returns {string} Race market group id.
     * @function Mojito.Services.SportsContent.MarketGroups.utils.getRaceMarketGroup
     */
    static getRaceMarketGroupId(marketGroups = []) {
        return marketGroups.find(e => e.type === GROUP_TYPES.RACE_MARKET)?.id;
    }

    /**
     * Returns aggregated market info that lists the given market id in its markets.
     *
     * @param {Array<Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo>} marketInfos - Market infos list.
     * @param {string} marketId - Market (line) id.
     *
     * @returns {Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo|undefined} Matching market info, if any.
     * @function Mojito.Services.SportsContent.MarketGroups.utils.findMarketInfoByMarketId
     */
    static findMarketInfoByMarketId(marketInfos, marketId) {
        return marketInfos?.find(marketInfo =>
            marketInfo.markets?.some(market => market?.id === marketId)
        );
    }

    /**
     * Builds early payout for a game line by merging `earlyPayout.rules` from each aggregated market info
     * that owns a game-line market, then unifying rules so each rule id appears only once.
     *
     * @param {Array<Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo>} marketInfos - Market infos list.
     * @param {Array<string|number>} gameLineMarketIds - Market line ids that belong to the game line.
     *
     * @returns {{ rules: Array<object> }|undefined} Shape assignable to {@link Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo#earlyPayout}, or undefined when no contributing market info has early payout.
     * @function Mojito.Services.SportsContent.MarketGroups.utils.createGameLineEarlyPayout
     */
    static createGameLineEarlyPayout(marketInfos, gameLineMarketIds) {
        const gameLineMarketInfos = gameLineMarketIds.map(marketId => {
            const marketInfo = MarketGroupsUtils.findMarketInfoByMarketId(marketInfos, marketId);
            return {
                id: marketId,
                earlyPayout: marketInfo?.earlyPayout,
            };
        });
        const hasEarlyPayout = gameLineMarketInfos.some(info => info.earlyPayout);
        const mergedRules = gameLineMarketInfos
            .map(gameLineMarketInfo => gameLineMarketInfo.earlyPayout?.rules)
            .filter(rules => rules?.length)
            .flat();
        const uniqueRules = uniqBy(mergedRules, rule => rule.id);
        return hasEarlyPayout ? { rules: uniqueRules } : undefined;
    }
}

/**
 * @typedef {Function} MarketGroupFilter
 * @param {object} marketGroup - Market group item.
 * @param {object} eventItem - The event object.
 * @returns {boolean} True if the group should belong to particular category, false otherwise.
 */
